/**
 * Provider-redirect bookkeeping.
 *
 * Stripe and PayPal host their own payment screens, so we cannot fire an
 * event *on* their page. What we can measure precisely is:
 *   - `redirect_started` — we handed the visitor to the provider,
 *   - `provider_checkout_returned` — they came back to us at all,
 *   - `provider_checkout_abandoned` — they came back to a page that is not a
 *     completion page, i.e. they saw the payment screen and did not pay.
 *
 * That distinction is what separates "checkout/account friction" (they never
 * reached the provider) from "trust or value objection" (they reached the
 * payment screen and stopped).
 */
const KEY = "rt_provider_redirect";

export type CheckoutFunnel = "membership";

type Pending = {
  funnel: CheckoutFunnel;
  provider: "stripe" | "paypal";
  at: number;
};

export function markProviderRedirect(
  funnel: CheckoutFunnel,
  provider: "stripe" | "paypal",
) {
  try {
    const payload: Pending = { funnel, provider, at: Date.now() };
    sessionStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* storage may be unavailable */
  }
}

/** Read and clear the pending redirect, if any. */
export function consumeProviderRedirect(): Pending | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);
    return JSON.parse(raw) as Pending;
  } catch {
    return null;
  }
}

export function peekProviderRedirect(): Pending | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Pending) : null;
  } catch {
    return null;
  }
}
